import { Helmet } from 'react-helmet-async'
import { BreadcrumbsJsonLd } from '@/components/seo/Breadcrumbs'
import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowRight, CalendarPlus } from 'lucide-react'
import { trackEvent } from '@/lib/analytics'
import { TableOfContents } from '@/components/sections/table-of-contents'

type ServiceSection = {
  id: string
  title: string
  subtitle: string
  description: string
  endtitle: string
  linkPath: string
}

const serviceSections: ServiceSection[] = [
  {
    id: 'fertility-preconception',
    title: 'Preconception Care & Fertility Support',
    subtitle: 'Fertility work at Raintree is about more than a single cycle. Renee supports people who are trying to conceive naturally or with IVF/ICSI, paying attention to:',
    description: 'We work with you to optimize your health before conception, supporting natural fertility and complementing IVF treatments. Our approach considers your full health picture, including cycle regularity, stress levels, sleep quality, and overall wellbeing.',
    endtitle: 'Comprehensive fertility support that addresses the whole person.',
    linkPath: '/what-we-help-with/fertility-preconception'
  },
  {
    id: 'pregnancy-postpartum',
    title: 'Pregnancy & Postpartum Support',
    subtitle: 'Pregnancy is a big physical and emotional transition. This pillar covers care in all trimesters and into the postpartum months.',
    description: 'From morning sickness in the first trimester to birth preparation and postpartum recovery, we provide gentle, supportive care tailored to each stage of your journey. Our treatments can help with common pregnancy discomforts, support optimal positioning for birth, and aid in postnatal healing.',
    endtitle: 'Treatment is always adjusted to your stage of pregnancy or postpartum, and can be coordinated with advice from your midwife, GP or obstetric team.',
    linkPath: '/what-we-help-with/pregnancy-postpartum'
  },
  {
    id: 'menstrual-hormonal',
    title: 'Menstrual & Hormonal Health',
    subtitle: 'Cycles are one of the main ways the body speaks. Renee supports teens and adults with menstrual and hormonal concerns such as:',
    description: 'Whether you\'re dealing with painful periods, irregular cycles, severe PMS, or navigating perimenopause, we work to restore balance to your hormonal system. Our treatments address both the physical symptoms and the underlying patterns that contribute to hormonal imbalances.',
    endtitle: 'Appointments are a space to map out what is happening, link hormones with stress, sleep and digestion, and work towards cycles that feel more predictable and manageable.',
    linkPath: '/what-we-help-with/menstrual-hormonal-health'
  },
  {
    id: 'stress-sleep-digestive',
    title: 'Stress, Sleep & Digestive Balance',
    subtitle: 'Underneath many symptoms there is a nervous system and a digestive system that need attention.',
    description: 'Stress, sleep, and digestion are deeply interconnected. We address these foundational aspects of health that often underlie other concerns. Our treatments help calm the nervous system, improve sleep quality, and support healthy digestive function, creating a solid foundation for overall wellbeing.',
    endtitle: 'Sessions are a chance to pause, regulate and reset while gently working on restoring more stable energy, sleep and digestion over time.',
    linkPath: '/what-we-help-with/stress-sleep-digestion'
  },
  {
    id: 'ongoing-womens-health',
    title: 'Ongoing Women\'s Health & Long-Term Care',
    subtitle: 'Some people come for a specific concern and stay because they value having a steady practitioner over time.',
    description: 'We build long-term therapeutic relationships, supporting you through different life stages and transitions. Having continuity of care with someone who knows your health history allows for deeper understanding and more effective treatment over time.',
    endtitle: 'Long-term care can involve blocks of more intensive treatment, followed by periods of lighter, maintenance-style support when things are more stable. Together you and Renee can review what you need at each stage and adjust the plan accordingly.',
    linkPath: '/what-we-help-with/womens-health-long-term-care'
  },
  {
    id: 'other-concerns',
    title: 'Other Concerns',
    subtitle: 'While Raintree\'s main focus is women\'s health, fertility and hormones, some people also come for support with:',
    description: 'We recognize that health concerns don\'t always fit into neat categories. Whether you\'re dealing with tension-related pain, chronic headaches, complex symptom patterns, or seeking supportive care alongside medical treatment, we work with you to address your unique situation.',
    endtitle: 'If your situation doesn\'t seem to fit any of the areas above, you\'re still welcome to get in touch. A short conversation or a free 15-minute phone consult can help clarify whether this style of care is likely to be helpful for you, or whether another type of support might be a better first step.',
    linkPath: '/what-we-help-with/other-concerns'
  }
]

const tocItems = [
  { id: 'introduction-section', title: 'Key Areas of Focus' },
  { id: 'fertility-preconception', title: 'Preconception Care & Fertility Support' },
  { id: 'pregnancy-postpartum', title: 'Pregnancy & Postpartum Support' },
  { id: 'menstrual-hormonal', title: 'Menstrual & Hormonal Health' },
  { id: 'stress-sleep-digestive', title: 'Stress, Sleep & Digestive Balance' },
  { id: 'ongoing-womens-health', title: 'Ongoing Women\'s Health & Long-Term Care' },
  { id: 'other-concerns', title: 'Other Concerns' },
  { id: 'not-sure-section', title: 'Not Sure Where You Fit?' },
]

export default function WhatWeHelpWithPage() {
  const navigate = useNavigate()

  const openBooking = (source?: string) => {
    if (source) {
      trackEvent('book_appointment_click', { location: source })
    }
    if ((window as any).openBookingModal) {
      ;(window as any).openBookingModal(source)
    }
  }

  return (
    <main className="min-h-screen bg-gray-50">
      <Helmet>
        <title>What We Help With | Acupuncture & Chinese Medicine | Raintree Chinese Medicine | Noosa & Maroochydore | Sunshine Coast</title>
        <meta name="description" content="Learn about acupuncture and Chinese medicine services at Raintree Chinese Medicine in Noosa and Maroochydore including Preconception Care & Fertility Support, Pregnancy & Postpartum Support, Menstrual & Hormonal Health, Stress, Sleep & Digestive Balance, and more." />
        <meta name="keywords" content="acupuncture Noosa, acupuncture Maroochydore, Chinese medicine Sunshine Coast, fertility acupuncture, women's health acupuncture, pregnancy acupuncture, hormonal health, Raintree Chinese Medicine, Dr Renee Knott, what we help with" />
        <link rel="canonical" href="https://raintreechinesemedicine.com.au/what-we-help-with" />
        <meta property="og:title" content="What We Help With | Acupuncture & Chinese Medicine | Raintree Chinese Medicine | Noosa & Maroochydore | Sunshine Coast" />
        <meta property="og:description" content="Learn about acupuncture and Chinese medicine services at Raintree Chinese Medicine in Noosa and Maroochydore including Preconception Care & Fertility Support, Pregnancy & Postpartum Support, Menstrual & Hormonal Health, Stress, Sleep & Digestive Balance, and more." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://raintreechinesemedicine.com.au/what-we-help-with" />
        <meta property="og:image" content="https://tdjwqdpgnmzhfcqsfyjh.supabase.co/storage/v1/object/sign/images/general/Raintree-Chinese-Medicine-Renee.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wYzVlMGM1NS1lYzI2LTRkMTYtOTc2MS1iZDQ1MWJhMGJiYjEiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJpbWFnZXMvZ2VuZXJhbC9SYWludHJlZS1DaGluZXNlLU1lZGljaW5lLVJlbmVlLnBuZyIsImlhdCI6MTc2NTE4MzkxOCwiZXhwIjoyMDIxNzkxOTE4fQ.-w4pg5Ka_Qt5M94A-DmuvJDiGP7a4DljS5sOLKvfAfE" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="What We Help With | Acupuncture & Chinese Medicine | Raintree Chinese Medicine | Noosa & Maroochydore | Sunshine Coast" />
        <meta name="twitter:description" content="Learn about acupuncture and Chinese medicine services at Raintree Chinese Medicine in Noosa and Maroochydore including Preconception Care & Fertility Support, Pregnancy & Postpartum Support, Menstrual & Hormonal Health, Stress, Sleep & Digestive Balance, and more." />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'What We Help With',
            description: 'Learn about acupuncture and Chinese medicine services at Raintree Chinese Medicine in Noosa and Maroochydore on the Sunshine Coast, including fertility, pregnancy, women\'s health, hormonal health and more.',
            url: 'https://raintreechinesemedicine.com.au/what-we-help-with',
            mainEntity: {
              '@type': 'MedicalBusiness',
              name: 'Raintree Chinese Medicine',
              description: 'Acupuncture and Chinese medicine clinic offering women\'s health, fertility, pregnancy and long-term care services.',
              address: [
                {
                  '@type': 'PostalAddress',
                  streetAddress: '31 Burgess Drive',
                  addressLocality: 'Tewantin',
                  addressRegion: 'QLD',
                  postalCode: '4565',
                  addressCountry: 'AU'
                },
                {
                  '@type': 'PostalAddress',
                  streetAddress: 'Ground Level 5/55 Plaza Parade',
                  addressLocality: 'Maroochydore',
                  addressRegion: 'QLD',
                  postalCode: '4558',
                  addressCountry: 'AU'
                }
              ],
              areaServed: [
                'Noosa',
                'Noosa Heads',
                'Noosaville',
                'Tewantin',
                'Maroochydore',
                'Sunshine Coast',
                'Buderim',
                'Bli Bli',
                'Cotton Tree',
                'Mooloolaba'
              ],
              medicalSpecialty: [
                'Women\'s Health',
                'Fertility Treatment',
                'Pregnancy Care',
                'Acupuncture',
                'Chinese Medicine',
                'Hormonal Health',
                'IVF Support',
                'Menstrual Health',
                'Postpartum Care',
                'Perimenopause',
                'Stress Management',
                'Sleep Disorders',
                'Digestive Health'
              ]
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: 'Home',
                  item: 'https://raintreechinesemedicine.com.au'
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: 'What We Help With',
                  item: 'https://raintreechinesemedicine.com.au/what-we-help-with'
                }
              ]
            }
          })}
        </script>
      </Helmet>
      <h1 className="sr-only">What We Help With</h1>
      <BreadcrumbsJsonLd />
      
      {/* Table of Contents */}
      <TableOfContents items={tocItems} />

      {/* Hero Section */}
      <section className="bg-[rgb(132,137,116)] py-16 lg:py-40 px-4 lg:px-0">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-6xl mx-auto"
          >
            {/* Mobile: Stack layout */}
            <div className="lg:hidden text-center">
              <h1 className="text-2xl md:text-3xl mb-4 text-white">
                What We Help With
              </h1>
              <p className="text-white/90 sm:w-2/3 mx-auto mb-6 sm:mb-12 text-sm sm:text-base">
                Explore the areas we specialize in and learn how Chinese Medicine and acupuncture can support your health journey.
              </p>
              <div className="flex flex-col gap-3 mb-4 sm:w-1/2 mx-auto">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('noosa_clinic')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Noosa Clinic
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('maroochydore_clinic')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Maroochydore Clinic
                  </span>
                </Button>
              </div>
              <Button
                variant="outline"
                className="w-full border-white text-white hover:bg-white/10 sm:w-1/2 mx-auto"
                onClick={() => openBooking('free_consult_header')}
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                  Free 15-minute Phone Consult
                </span>
              </Button>
            </div>

            {/* Desktop: Side-by-side layout */}
            <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
              <div>
                <h1 className="text-3xl lg:text-4xl mb-4 text-white">
                  What We Help With
                </h1>
                <p className="text-white/90 text-base lg:text-lg">
                    Raintree Chinese Medicine offers acupuncture and Chinese herbal medicine with a focus on women's health, fertility, pregnancy and long-term care, among other areas of concern.
                </p>
              </div>
              <div className="flex flex-col gap-3 mx-auto px-4">
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('noosa_clinic')}
                > 
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Noosa Clinic
                  </span>
                </Button>
                <Button 
                  className="w-full bg-white text-gray-800 hover:bg-gray-100"
                  onClick={() => openBooking('maroochydore_clinic')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Book Maroochydore Clinic
                  </span>
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-white text-white hover:bg-white/80"
                  onClick={() => openBooking('free_consult_header')}
                >
                  <span className="inline-flex items-center gap-2">
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    Free 15-minute Phone Consult
                  </span>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Introduction Section */}
      <section id="introduction-section" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] text-center">
              Key areas of focus
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 font-medium mb-4 text-left text-base lg:text-md">
            Raintree is built around Renee's deep focus on the following key areas of care: 
            </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 mb-8 text-left text-base lg:text-md">
                <li>
                  <a 
                    href="#fertility-preconception" 
                    className="no-underline text-gray-600 hover:underline cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault()
                      document.getElementById('fertility-preconception')?.scrollIntoView({ behavior: 'smooth' })
                    }}
                  >
                    Fertility & preconception care →
                  </a>
                </li>
                <li>
                  <a 
                    href="#pregnancy-postpartum" 
                    className="no-underline text-gray-600 hover:underline cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault()
                      document.getElementById('pregnancy-postpartum')?.scrollIntoView({ behavior: 'smooth' })
                    }}
                  >
                    Pregnancy & postpartum support →
                  </a>
                </li>
                <li>
                  <a 
                    href="#menstrual-hormonal" 
                    className="no-underline text-gray-600 hover:underline cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault()
                      document.getElementById('menstrual-hormonal')?.scrollIntoView({ behavior: 'smooth' })
                    }}
                  >
                    Menstrual & hormonal health →
                  </a>
                </li>
                <li>
                  <a 
                    href="#stress-sleep-digestive" 
                    className="no-underline text-gray-600 hover:underline cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault()
                      document.getElementById('stress-sleep-digestive')?.scrollIntoView({ behavior: 'smooth' })
                    }}
                  >
                    Stress, sleep & digestive balance →
                  </a>
                </li>
                <li>
                  <a 
                    href="#ongoing-womens-health" 
                    className="no-underline text-gray-600 hover:underline cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault()
                      document.getElementById('ongoing-womens-health')?.scrollIntoView({ behavior: 'smooth' })
                    }}
                  >
                    Ongoing women's health & long-term care →
                  </a>
                </li>
                <li>
                  <a 
                    href="#other-concerns" 
                    className="no-underline text-gray-600 hover:underline cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault()
                      document.getElementById('other-concerns')?.scrollIntoView({ behavior: 'smooth' })
                    }}
                  >
                    Other Concerns →
                  </a>
                </li>
              </ul>
              <p className="text-gray-600 text-left text-base lg:text-md mb-0">
              Many people fit into more than one of these areas, and as such, sessions are always tailored to your individual needs.
            </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Service Sections */}
      {serviceSections.map((service, index) => (
        <section key={service.id} id={service.id} className={`py-20 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
                {service.title}
              </h2>
              <div className="pl-4 lg:pl-[16%] max-w-3xl">
                <p className="text-gray-600 font-medium mb-4 text-left">
                {service.subtitle}
              </p>
              {service.id === 'fertility-preconception' ? (
                <div className="text-gray-600 mb-8 text-left">
                  <ul className="list-disc list-inside space-y-2 mb-6">
                    <li>Cycles and bleeding patterns over time</li>
                    <li>Energy, sleep and digestion</li>
                    <li>Stress and emotional load during fertility treatment</li>
                  </ul>
                  <p className="text-left">
                    Acupuncture and Chinese medicine in this context aim to support your body as you move through investigations, stimulation cycles, transfers and the waiting periods in between. The focus is on helping you feel steadier, more supported and more informed within your overall fertility plan.
                  </p>
                </div>
              ) : service.id === 'pregnancy-postpartum' ? (
                <div className="text-gray-600 mb-8 text-left">
                  <p className="font-medium mb-4">
                    People often come for support with:
                  </p>
                  <ul className="list-disc list-inside space-y-2 mb-6">
                    <li>Common pregnancy symptoms such as nausea, sleep difficulty, discomfort or anxiety</li>
                    <li>Late-pregnancy comfort and birth preparation</li>
                    <li>Recovery after birth, including energy, mood and bleeding patterns</li>
                    <li>The early weeks and months of parenting, when sleep is disrupted and everything feels new</li>
                  </ul>
                </div>
              ) : service.id === 'menstrual-hormonal' ? (
                <div className="text-gray-600 mb-8 text-left">
                  <ul className="list-disc list-inside space-y-2 mb-6">
                    <li>Painful, heavy or irregular periods</li>
                    <li>PMS/PMDD and mood changes around the cycle</li>
                    <li>Perimenopausal symptoms – sleep disturbance, hot flushes, night sweats or cycle changes</li>
                    <li>Cycle-related headaches, low mood or energy shifts</li>
                  </ul>
                </div>
              ) : service.id === 'stress-sleep-digestive' ? (
                <div className="text-gray-600 mb-8 text-left">
                  <p className="font-medium mb-4">
                    This pillar includes care for people who feel:
                  </p>
                  <ul className="list-disc list-inside space-y-2 mb-6">
                    <li>Constantly "on", wired but tired or close to burnout</li>
                    <li>Overwhelmed by caring responsibilities or work</li>
                    <li>Stuck in poor sleep patterns – trouble falling asleep or staying asleep</li>
                    <li>Upset in their digestion, with IBS-type symptoms, bloating or bowel changes linked to stress</li>
                  </ul>
                </div>
              ) : service.id === 'ongoing-womens-health' ? (
                <div className="text-gray-600 mb-8 text-left">
                  <p className="font-medium mb-4">
                    This pillar is about continuity – being supported through several phases, for example:
                  </p>
                  <ul className="list-disc list-inside space-y-2 mb-6">
                    <li>Menstrual and hormonal repair</li>
                    <li>Preconception care and fertility</li>
                    <li>Pregnancy and postpartum recovery</li>
                    <li>Perimenopause and later life</li>
                  </ul>
                </div>
              ) : service.id === 'other-concerns' ? (
                <div className="text-gray-600 mb-8 text-left">
                  <ul className="list-disc list-inside space-y-2 mb-6">
                    <li>Musculoskeletal Pain - Joint, Muscle, Nerve </li>
                    <li>Headaches or migraines</li>
                    <li>General stress-related symptoms that don't fit neatly into one category</li>
                    <li>Supportive care during and after Cancer treatment</li>
                  </ul>
                </div>
              ) : (
                <p className="text-gray-600 mb-8 text-left">
                  {service.description}
                </p>
              )}
                <p className="text-gray-600 mb-8 text-left">
                {service.id === 'fertility-preconception' ? (
                  <>
                    If you're booking for the first time for fertility or preconception care, please choose an{' '}
                    <button
                      onClick={() => openBooking('fertility_initial_consult')}
                      className="underline font-normal text-gray-600 hover:text-gray-800 cursor-pointer"
                    >
                      Initial Consultation & Treatment
                    </button>
                    .
                  </>
                ) : service.id === 'other-concerns' ? (
                  <>
                    If your situation doesn't seem to fit any of the areas above, you're still welcome to get in touch. A short conversation or a{' '}
                    <button
                      onClick={() => openBooking('other_concerns_free_consult')}
                      className="underline font-normal text-gray-600 hover:text-gray-800 cursor-pointer"
                    >
                      free 15-minute phone consult
                    </button>
                    {' '}can help clarify whether this style of care is likely to be helpful for you, or whether another type of support might be a better first step.
                  </>
                ) : (
                  service.endtitle
                )}
              </p>
                <div className="text-left">
                <Link
                  to={service.linkPath}
                    state={{ fromParent: true }}
                  className="text-[#c5a574] hover:text-[#DBB981] font-medium inline-flex items-center gap-1 transition-colors text-base"
                >
                  Learn more about {service.title}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      ))}

      {/* Not Sure Where You Fit? Section */}
      <section id="not-sure-section" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl mb-8 text-[#3f4141] max-w-3xl mx-auto text-center">
              Not Sure Where You Fit?
            </h2>
            <div className="pl-4 lg:pl-[16%] max-w-3xl">
              <p className="text-gray-600 mb-6 text-left">
              It's completely normal to read through this page and feel like you sit in more than one category.
            </p>
              <p className="text-gray-600 font-medium mb-4 text-left">
              If you're new to Raintree and:
            </p>
              <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 text-left">
              <li>You're not sure which area to choose, or</li>
              <li>You're working with a more complex picture</li>
            </ul>
              <p className="text-gray-600 mb-12 text-left">
              …the best starting point is simply to book an{' '}
              <button
                onClick={() => openBooking('not_sure_initial_consult')}
                className="underline font-normal text-gray-600 hover:text-gray-800 cursor-pointer"
              >
                Initial Consultation & Treatment
              </button>
              {' '}or book a{' '}
              <button
                onClick={() => openBooking('not_sure_free_consult')}
                className="underline font-normal text-gray-600 hover:text-gray-800 cursor-pointer"
              >
                free 15 minute phone consult
              </button>
              {' '}and we'll map things out together.
            </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[rgb(132,137,116)] py-12 lg:py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-3xl lg:text-4xl font-normal text-white mb-8">
              Are you ready to begin your journey to better health?
            </h2>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button
                variant="outline"
                className="w-full sm:w-auto px-8 border-2 border-white text-white hover:bg-white hover:text-gray-600"
                onClick={() => {
                  trackEvent('book_appointment_click', { location: 'what_we_help_with_view_appointments' })
                  navigate('/appointments')
                }}
              >
                <span className="inline-flex items-center gap-2">
                  View Appointments & Fees
                  <ArrowRight className="h-4 w-4" />
                </span>
              </Button>
              <Button
                className="w-full sm:w-auto px-8 bg-white text-gray-600 hover:bg-white/80"
                onClick={() => openBooking('what_we_help_with_free_consult')}
              >
                <span className="inline-flex items-center gap-2">
                  <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                  Book a free 15-minute phone consult
                </span>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

    </main>
  )
}

